import { DayOfWeek } from '@core/generated/gql-types';

// TODO: Add these to the generated types
export enum ApplyAcceptMode {
  ApplyOnly = 'APPLY_ONLY',
  AcceptOnly = 'ACCEPT_ONLY',
  Both = 'BOTH',
}

/**
 * How often a worker submits time.
 * DAY uses live check-in and check-out, including the time window and geo-fence.
 * WEEK, FORTNIGHT, and MONTH use the period timesheet and do not apply those rules.
 */
export enum TimesheetSubmissionFrequency {
  Day = 'DAY',
  Week = 'WEEK',
  Fortnight = 'FORTNIGHT',
  Month = 'MONTH',
}

export const isDailyTimesheetSubmission = (
  frequency: TimesheetSubmissionFrequency | null | undefined,
): boolean =>
  (frequency ?? TimesheetSubmissionFrequency.Day) ===
  TimesheetSubmissionFrequency.Day;

export interface IInductionLink {
  label: string;
  url: string;
}

export interface ICoreLoopConfigurationItems {
  /**
   * Posting Jobs
   */

  // Client Users cannot post jobs
  postingRequiresClientAdmin: boolean;
  // Tenant Admin can post jobs on Clients Behalf
  tenantAdminCanPost: boolean;
  // The minimum amount of time, before a job start time, that a job can be posted for
  // Set to 0 to allow jobs to be posted anytime, even in the past.
  minimumLeadTimeForJobInMinutes: number;

  /**
   * Accepting and Assigning
   */

  // Allow the Worker to apply/accept a job, even if they don't meet the requirements
  canBypassRequirements: boolean;
  // Allow multiple workers to apply for the same job
  // Accept vs Apply vs Both?
  shouldAllowMultipleApplications: boolean;
  // Controls whether jobs allow Apply only, Accept only, or Both
  // APPLY_ONLY: Jobs can only be applied to (workers must apply, cannot accept directly)
  // ACCEPT_ONLY: Jobs can only be accepted (workers can accept directly, no applications)
  // BOTH: Jobs can be either applied to or accepted (client decides at job posting time)
  shouldAllowApplyAndAccept: ApplyAcceptMode;
  // Workers can attach an optional comment when applying. Default false.
  enableJobApplicationComments?: boolean;
  // After the buffer time has passed, auto assign to one of the applied workers
  shouldAutoAssignToAppliedWorkers: boolean;
  // Prefer previously hired workers, as in if even 1 of them applies, they get it.
  shouldPreferPreviouslyHiredWorkers: boolean;
  // Prefer Favourited workers. This will override the previous preference of previously hired workers.
  shouldPreferFavouritedWorkers: boolean;
  // Will auto assign to a preferred worker (Previously hired and/or Favourited) if they apply.
  shouldAutoAssignWhenPreferredWorkerApplies: boolean;
  // The amount of time to buffer before auto assigning to a worker
  // This is only used if shouldAllowMultipleApplications is set to true
  // This cannot be 0 if shouldAllowMultipleApplications is true
  // This will default to 10 minutes if not set.
  acceptanceBufferTimeInMinutes: number;
  // Controls who can assign workers to jobs
  // true: TenantAdmin and HQ Admin can assign workers
  // false: Only HQ Admin can assign workers
  adminCanAssignToAnyWorker: boolean;

  /**
   * Notifications
   */
  // Notify Workers when a job is posted
  shouldNotifyWorkersWhenJobPosted: boolean;
  // Send job posted notifications in chunks, aka not all at once.
  shouldSendJobPostedNotificationsInChunks: boolean;
  // The minimum number of workers to notify per chunk
  minimumWorkersToNotifyPerChunk: number;
  // The amount of time to wait between chunks, in minutes
  preferredWaitTimePerChunk: number;

  /**
   * Notification of Job Lifecycle Events
   */

  shouldNotifyClientWhenJobAccepted: boolean;
  shouldNotifyClientWhenJobAppliedTo: boolean;
  shouldNotifyClientWhenJobRejected: boolean;
  shouldNotifyClientWhenJobCancelled: boolean;
  shouldNotifyClientWhenJobCompleted: boolean;

  /**
   * Reviews
   */
  /** Post-completion worker rating prompt on the client app. Default false. */
  enableClientWorkerRatingPrompt: boolean;

  /**
   * Fancy Stuff
   */
  // Override the radius for the job
  shouldOverrideRadius: boolean;
  // The radius to override the job with
  overrideRadius: number;

  /**
   * Timesheets
   */
  // Allow workers to clock in and out
  shouldUseCICOForTimesheets: boolean;
  // DAY keeps the clock time window and geo-fence. Other values use the period timesheet.
  timesheetSubmissionFrequency: TimesheetSubmissionFrequency;
  // A real calendar date used as the historical starting point. Format is YYYY-MM-DD.
  payRunCycleAnchorDate: string; // e.g. 2026-10-05
  // Approve timesheets in one block per candidate from the Timesheets page.
  // When false, the per-job timesheet approval workflow is used.
  shouldUseBlockTimesheetApproval?: boolean;
  // The amount of time to allow for a break, in minutes
  // This time will not reduce the total hours, and it will be assumed user takes this break
  paidBreakTimeInMinutes: number;
  // The amount of time to allow for an unpaid break, in minutes
  // this time will always be subtracted from the raw time, and always be assumed to have been taken
  unpaidBreakTimeInMinutes: number;
  // The standard shift hours for the tenant. Normally 7.6 in Australia
  standardShiftHours: number;
  // if the raw duration is less than this, then total hours will be set to this
  // Breaks will *not* subtract from this if this is set.
  // this is the minimum amount a worker can be paid for
  minimumHoursForShift: number;
  // If the total time is greater than this, the shift will require approval.
  maximumHoursForShift: number;
  // Display the clock in button x minutes before the job start time
  displayClockInButtonBeforeJobStartTimeInMinutes: number;
  // The buffer time for clock in and clock out. This is the time before and after the job start time that the worker can clock in and out.
  clockInClockOutBufferInMinutes: number;

  /**
   * Timesheet things that require Approvals
   */

  // Allow workers to clock in early
  allowEarlyClockIn: boolean;
  // Allow workers to clock out late
  allowLateClockOut: boolean;
  // If this is false then the user will not earn overtime for clocking out late or clocking in early
  overtimeCausesOvertimeMultiplier: boolean;
  // Overtime seconds will be billed at this multipler.
  overtimeMultiplier: number;
  // Overtime pay uses minute chunks, instead of seconds. Work needs to be rounded to the nearest minute, rounded down. Will not cause overtime multiplier if time is less than chunk time.
  overtimePayUsesMinuteChunks: boolean;
  // The amount of minutes per chunk. Eg. if chunks are 15 minutes, then 14 minutes and 59 seconds will be rounded down to 14 minutes and not be paid. If user works 20 min it'll be rounded down to 15 min and only earn 1 chunk
  overtimeChunksInMinutes: number;
  // Allow workers to CICO from anywhere on earth
  allowCICOOutsideAllowedArea: boolean;
  // User must be within this distance of the job location to CICO. Uses client address, and radius from centre. Use a bigger number for bigger client campsus
  minimumDistanceForCICOinMeters: number;
  // If true, then timesheets will still require approval before they will get processed
  outsideCICORequiresApproval: boolean;
  // Allow workers to manually enter timesheets
  allowManualTimesheets: boolean;
  // If true, then timesheets will require approval before they will get processed
  manualTimesheetsRequireApproval: boolean;
  // Allow workers to CICO outside the allowed time
  allowCICOOutsideAllowedTime: boolean;

  /**
   * Assets
   */

  // [DRIVER_LICENSE, FIRST_AID, CERTIFICATE, etc]
  requiredAssets: string[];

  /**
   * Margin
   */

  // The difference between what the worker gets paid, and what the client gets charged.
  // default is 0.15. Use a number between 0-1.
  // can be overwritten at a client level.
  marginPercentage: number;
  // Allow the tenant to override the margin percentage per client. default to false
  allowClientMarginOverride: boolean;
  // When false, fixed charge type is hidden for clients in the portal
  allowClientFixedChargeType?: boolean;
  // When false, rate card charge type is hidden for clients in the portal
  allowClientRateCardChargeType?: boolean;

  // Allow the tenant to allow the client budget to be below the minimum rate
  // What this means is that a client can post a job, or have a job accepted by a worker, even if
  // the budget is far far too low.
  // This is really dangerous and could have legal ramifications.
  // This is defaulted to false
  allowClientBudgetToBeBelowMinimumRate: boolean;
  // Force the client budget to be above the minimum rate
  // This is if a client has posted too low, should we allow workers to accept it even?
  // This is dangerous because the client is on the hook for more money than they expected.
  // This is defaulted to false
  forceClientBudgetToBeAboveMinimumRate: boolean;

  /**
   * Job Posting Questionnaire
   */

  // If true, the client user will be required to answer a questionnaire when posting a job
  // The questionnaire questions are configured via the portal and fetched via getJobPostingQuestionnaire
  // The answers will be included in the job description shown to candidates
  tenantRequiresPostingQuestionnaire: boolean;

  /**
   * Sensible Limits
   */

  // If true, then the maximum hourly rate will be enforced. If false, then a worker could potentially be paid a trillion dollars an hour if the client set the budget to that.
  // Should always be true. Defaults to true.
  enforceMaximumHourlyRate: boolean;
  // The maximum hourly rate that can be set for a job.
  maximumHourlyRate: number;
  maxDaysPerWeek: number;
  // This is how many 'Day Sequences' to spawn in advanced for a contract job.
  // 20 just means that at any time, 20 future day sequences will be created based on the jobs scheduleDefinitions
  numberOfFutureDaysToSpawnForContractJob: number;

  /**
   *
   * Cancelling jobs
   *
   */

  // If true, then a client can cancel a job that has been accepted.
  // If false, then a client cannot cancel a job that has been accepted.
  // This is defaulted to true
  clientsCanCancelFilledJob: boolean;

  // The minimum amount of lead time a job must have before it can be cancelled, if its filled
  // This prevents clients cancelling jobs at the last minute.
  // defaults to 120 minutes (2 hours)
  minimumBufferForClientCancellingFilledJobInMinutes: number;

  /**
   * Support
   */
  supportContactNumber: string;
  inductionLinks?: IInductionLink[];
  // When true, the worker Account screen shows Tax, Banking & Super. Default false.
  displayBankTaxAndSuper?: boolean;

  /**
   * Pay Rates
   */
  // If true, then clients can see the pay rates for a job
  // If false, then clients cannot see the pay rates for a job
  // This is defaulted to false
  clientsCanSeePayRates: boolean;

  // When true, client app / client portal / agency job application lists show
  // the candidate suburb. Default false.
  displayCandidateSuburbForClients?: boolean;

  // If true, then workers can see the pay rates for a job in the job card as well as est pay on job detail page
  // If false, then workers cannot see the pay rates for a job in the job card as well as est pay on job detail page
  workersCanSeePayRates: boolean;

  // When true, tenant and HQ staff can override worker pay and client charge
  // rates while assigning a worker or accepting an application. Default false.
  enableCustomAssignmentRates: boolean;

  /**
   * Onboarding — hire / employment agreements
   */
  // When true, multi-step hire contracts in onboarding (per-tenant rollout). Default false.
  enableMultiContractOnboarding: boolean;

  /**
   * Client Portal
   */
  // When true, client invite emails include web portal signup instructions.
  enableClientPortal: boolean;

  // When true, ClientAdmin/ClientUser may edit future Unfilled/Draft jobs.
  // Default false.
  clientsCanEditJobs?: boolean;

  // When true, clients may see that contact detail for an assigned candidate.
  // Either flag shows the Candidate information block. Both default to false.
  clientsCanSeeAssignedCandidateEmail?: boolean;
  clientsCanSeeAssignedCandidatePhone?: boolean;

  // Asset tags client users can see when they fetch a worker's documents.
  // Empty means clients see none.
  clientVisibleAssets?: string[];

  // Allow clients to see protected assets
  allowClientsToSeeProtectedAssets: boolean;
}
